/**
 * CashbackDisplayService — Cashback Display Master (v1.12.313)
 *
 * Service déterministe (zero API call) qui centralise :
 *   1. Calcul du cashback exact (montant €) selon tier user + commission partenaire
 *   2. Formatage strings localisés (FR/EN) — "Récompense" / "Reward"
 *   3. Messages d'upsell vers tier supérieur (compact / medium / long)
 *   4. Estimations annuelles
 *   5. Push notifications
 *
 * RÈGLES STRICTES :
 *   - TOUJOURS afficher en MONTANT (€) — JAMAIS en %
 *   - Vocabulaire : "Récompense" plutôt que "Cashback" (marketing)
 *   - Marge Mytenue toujours ≥ 60% de la commission
 */

import { getCashbackBreakdown, getPartnerCommissionRate, getTierShare } from '../config/cashback';
import { CASHBACK_RATES } from '../config/CashbackConfig';
import { getUsdToEurSync } from './ExchangeRateService';

const isEN = (locale) => String(locale || '').toLowerCase().startsWith('en');

/** Format montant € selon locale. FR : "3,00€" — EN : "€3.00". */
export function formatEur(amount, locale = 'fr') {
  const n = Math.max(0, Number(amount) || 0);
  if (isEN(locale)) return `€${n.toFixed(2)}`;
  return `${n.toFixed(2).replace('.', ',')}€`;
}

/** Format montant USD selon locale. Toujours "$X.XX". */
export function formatUsd(amount) {
  const n = Math.max(0, Number(amount) || 0);
  return `$${n.toFixed(2)}`;
}

/** v1.12.336 — Récupère la devise d'un produit (depuis description JSON ou
 *  via le partner pour COUTR). Default EUR. */
export function getProductCurrency(product) {
  if (!product) return 'EUR';
  if (product.currency) return product.currency;
  if (product.partner === 'coutr') return 'USD';
  try {
    const desc = typeof product.description === 'string'
      ? JSON.parse(product.description)
      : product.description;
    if (desc?.currency) return desc.currency;
  } catch {}
  return 'EUR';
}

const TIER_LABEL = {
  free:    { fr: 'Free',    en: 'Free' },
  basic:   { fr: 'Basic',   en: 'Basic' },
  premium: { fr: 'Premium', en: 'Premium' },
  gold:    { fr: 'Gold',    en: 'Gold' },
};

function tierName(tier, locale) {
  const e = TIER_LABEL[tier] || TIER_LABEL.free;
  return isEN(locale) ? e.en : e.fr;
}

function nextTier(tier) {
  if (tier === 'free')    return 'basic';
  if (tier === 'basic')   return 'premium';
  if (tier === 'premium') return 'gold';
  return null;
}

/**
 * Cashback d'un produit pour un user. Retourne tous les strings prêts à afficher.
 *   → {amount, formatted, badge, label, primary, tooltip, upsell:{...}}
 */
export function displayProductReward(product, userTier = 'free', locale = 'fr') {
  // v1.12.336 — Pour les produits USD (COUTR), on convertit le prix en EUR
  //   avant d'appliquer commission_partner × tier_share. Le cashback final
  //   est en EUR (devise du wallet user).
  const currency = getProductCurrency(product);
  let amount = 0;
  if (currency === 'USD') {
    const usdRate = getUsdToEurSync(); // ex. 0.92
    const priceEur = (Number(product?.price) || 0) * usdRate;
    const commissionRate = getPartnerCommissionRate(product);
    const tierShare = getTierShare(userTier);
    amount = Math.round(priceEur * commissionRate * tierShare * 100) / 100;
  } else {
    const cb = getCashbackBreakdown(product, userTier);
    amount = cb?.cashbackEur || 0;
  }
  const formatted = formatEur(amount, locale);
  const name = tierName(userTier, locale);

  const out = {
    amount,
    formatted,
    badge: `💰 +${formatted}`,
    label: isEN(locale) ? `${name} reward` : `Récompense ${name}`,
    primary: isEN(locale)
      ? `💰 Get back ${formatted} on this purchase`
      : `💰 Récupérez ${formatted} sur cet achat`,
    tooltip: isEN(locale)
      ? `Get back ${formatted} after your purchase is validated`
      : `Récupérez ${formatted} après validation de votre achat`,
    upsell: null,
  };

  // Upsell vers tier supérieur si delta significatif (> 0,01 €)
  const target = nextTier(userTier);
  if (target && amount > 0) {
    // v1.12.336 — Pour USD : recompute avec la même conversion EUR
    let nextAmount = 0;
    if (currency === 'USD') {
      const usdRate = getUsdToEurSync();
      const priceEur = (Number(product?.price) || 0) * usdRate;
      const commissionRate = getPartnerCommissionRate(product);
      const nextShare = getTierShare(target);
      nextAmount = Math.round(priceEur * commissionRate * nextShare * 100) / 100;
    } else {
      const nextCb = getCashbackBreakdown(product, target);
      nextAmount = nextCb?.cashbackEur || 0;
    }
    const delta = nextAmount - amount;
    if (delta > 0.01) {
      const dFmt = formatEur(delta, locale);
      const nFmt = formatEur(nextAmount, locale);
      const nName = tierName(target, locale);
      out.upsell = {
        targetTier: target,
        delta,
        nextAmount,
        compact: isEN(locale) ? `+${dFmt} with ${nName}` : `+${dFmt} en ${nName}`,
        medium:  isEN(locale)
          ? `✨ With ${nName}, you'd get ${nFmt} (+${dFmt})`
          : `✨ Avec ${nName}, vous récupéreriez ${nFmt} (+${dFmt})`,
      };
    }
  }
  return out;
}

/**
 * Cashback d'un look complet (liste d'items).
 */
export function displayLookReward(items, userTier = 'free', locale = 'fr') {
  let total = 0;
  const breakdown = [];
  for (const it of items || []) {
    const cb = getCashbackBreakdown(it, userTier);
    const r = cb?.cashbackEur || 0;
    if (r > 0) {
      total += r;
      breakdown.push({ name: it.name || it.brand || '—', price: it.price, reward: r });
    }
  }
  total = Math.round(total * 100) / 100;
  const formatted = formatEur(total, locale);

  return {
    amount: total,
    formatted,
    badge: isEN(locale) ? `💰 Look reward: ${formatted}` : `💰 Récompense look : ${formatted}`,
    label: isEN(locale) ? 'To get back on this look' : 'À récupérer sur ce look',
    savingsMessage: isEN(locale)
      ? `✨ By buying this complete look, you get back ${formatted}`
      : `✨ En achetant ce look complet, vous récupérez ${formatted}`,
    breakdown,
  };
}

/**
 * Estimation annuelle de récompenses pour un budget mensuel donné.
 *   avgCommission : commission partenaire moyenne (default 10%)
 */
export function calculateAnnualEstimate(userTier = 'free', monthlySpend = 0, locale = 'fr', avgCommission = 0.10) {
  const annualSpend = Number(monthlySpend) * 12;
  const annualCommission = annualSpend * avgCommission;
  const share = CASHBACK_RATES[userTier] != null ? CASHBACK_RATES[userTier] : CASHBACK_RATES.free;
  const annualCashback = Math.round(annualCommission * share);
  const monthlyAvg = Math.round((annualCashback / 12) * 100) / 100;
  return {
    annualCashback,
    monthlyAvg,
    formatted: formatEur(annualCashback, locale),
    monthlyFormatted: formatEur(monthlyAvg, locale),
    mainLabel: isEN(locale) ? 'Estimated annual rewards' : 'Récompenses annuelles estimées',
    subLabel: isEN(locale)
      ? `On average ${formatEur(monthlyAvg, locale)}/month`
      : `Soit en moyenne ${formatEur(monthlyAvg, locale)}/mois`,
  };
}

/**
 * Exemples de récompenses par tier sur un panier donné (pour pricing table).
 *   amount: panier en €
 *   avgCommission: commission moyenne partenaire (default 10%)
 */
export function getTierRewardExamples(amount, locale = 'fr', avgCommission = 0.10) {
  const commission = amount * avgCommission;
  const tiers = ['free', 'basic', 'premium', 'gold'];
  return tiers.reduce((acc, t) => {
    const reward = Math.round(commission * (CASHBACK_RATES[t] || 0) * 100) / 100;
    acc[t] = formatEur(reward, locale);
    return acc;
  }, {});
}

/**
 * Push notification après un achat tracké.
 */
export function generateRewardPushNotification(amount, brand, locale = 'fr') {
  const formatted = formatEur(amount, locale);
  let body;
  if (isEN(locale)) {
    body = brand
      ? `${formatted} earned on your purchase at ${brand}!`
      : `${formatted} earned on your purchase!`;
  } else {
    body = brand
      ? `${formatted} récupérés sur votre achat chez ${brand} !`
      : `${formatted} récupérés sur votre achat !`;
  }
  return {
    title: isEN(locale) ? '✨ Mytenue reward' : '✨ Récompense Mytenue',
    body,
  };
}

/**
 * Wording du status d'une récompense (pending / validated / cancelled).
 */
export function getStatusLabel(status, locale = 'fr') {
  const en = isEN(locale);
  switch (String(status || '').toLowerCase()) {
    case 'validated':
    case 'confirmed':
      return en ? 'Validated' : 'Validée';
    case 'pending':
    case 'pending_validation':
      return en ? 'Pending' : 'En attente';
    case 'cancelled':
    case 'rejected':
      return en ? 'Cancelled' : 'Annulée';
    default:
      return en ? 'In progress' : 'En cours';
  }
}
